/**
 * The first run: choose the agent that answers (RFC 0337 S6), then the apps to
 * start with (RFC 0279 S6), and invite people where sharing is open
 * (RFC 0345 S14).
 *
 * A full-screen overlay over the project, not a panel and not a route. It is the
 * one moment in the app that is deliberately modal: the project behind it is
 * empty, so there is nothing to look at yet.
 *
 * It shows what is true on this Mac. Each agent Mod can drive carries its state
 * from the desktop shell's probe ("Ready", "Not logged in", "Not installed") and,
 * when it is not ready, the command that fixes it. The shell re-probes every ten
 * seconds, so a login done in a terminal turns the card ready without a reload.
 * The person ticks the ones they use, every ready one to start with: the composer
 * offers only those, and the first ticked is its default. In a browser nothing is probed,
 * and the cards carry no state rather than claim an agent is missing.
 *
 * The apps step lists the registry's Featured packages and installs the ticked
 * ones into the World, so a new World ends its first run with them in
 * Packages. Nothing starts ticked: they are suggestions, and an install is
 * something a person chose. While the registry has not answered, Next leads to
 * the step showing a loading line (Skip still works); only an empty or failed
 * answer skips the step, rather than show it empty.
 *
 * Skippable, because the fastest path into a project has to remain "just let me
 * in".
 */

import type { Repo } from '@automerge/automerge-repo'
import type { RegistryEntry } from '@mod/core/model'
import { Check } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  rememberComposerHarnesses,
  rememberComposerModel,
} from '../../../features/thread/Composer'
import { IconTile } from '../../../services/apps/AppIcon'
import { useProjectInviter } from '../../../services/sharing/projectInvite'
import { useHarnessStatuses } from '../../../services/world/useHarnessStatuses'
import { useLocalHarnesses } from '../../../services/world/useLocalHarnesses'
import {
  featuredApps,
  type InstallFailure,
  installFeaturedApps,
} from './featuredApps'
import {
  composerChoiceFor,
  defaultHarness,
  finishOnboarding,
  HARNESS_OPTIONS,
  type OnboardingChoices,
  type Readiness,
  readChoices,
  readiness,
  readyHarnesses,
  saveChoices,
} from './onboardingState'

export function OnboardingFlow({
  onDone,
  repo,
  projectId,
  featured = featuredApps,
  onInstallApps,
}: {
  onDone: () => void
  /** Where the picked apps install: the World, or this project without one. */
  repo?: Repo
  projectId?: string
  /** The registry's Featured list; tests pass a fake. An empty or failed
   *  answer skips the apps step. */
  featured?: () => Promise<RegistryEntry[]>
  /** Install these. Resolves with the ones that failed. Defaults to
   *  installing into `repo`; with neither, there is no apps step. */
  onInstallApps?: (entries: RegistryEntry[]) => Promise<InstallFailure[]>
}) {
  const install = useMemo(
    () =>
      onInstallApps ??
      (repo && projectId
        ? (entries: RegistryEntry[]) =>
            installFeaturedApps(repo, projectId, entries)
        : undefined),
    [onInstallApps, repo, projectId],
  )
  const [step, setStep] = useState<'agents' | 'apps'>('agents')
  // 'pending' until the registry answers; a failure reads as an empty list.
  const [picks, setPicks] = useState<RegistryEntry[] | 'pending'>(() =>
    install ? 'pending' : [],
  )
  const [installing, setInstalling] = useState(false)
  const [failed, setFailed] = useState<InstallFailure[]>([])
  // ONCE per mount. A caller passing a fresh function each render would
  // otherwise refetch on every render, and every fetch renders again.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see above
  useEffect(() => {
    if (!install) return
    let live = true
    featured()
      .then((list) => {
        if (live) setPicks(list)
      })
      .catch(() => {
        if (live) setPicks([])
      })
    return () => {
      live = false
    }
  }, [])
  // Seeded from previous choices, so `?onboarding=1` shows what was picked before.
  const [choices, setChoices] = useState<OnboardingChoices>(readChoices)
  const statuses = useHarnessStatuses()
  const local = useLocalHarnesses()

  const rows = useMemo(
    () =>
      HARNESS_OPTIONS.map((h) => ({
        ...h,
        readiness: readiness(
          statuses?.find((s) => s.id === h.id),
          statuses !== null,
        ),
      })),
    [statuses],
  )
  const anyReady = rows.some((r) => r.readiness?.ready)

  // Every ready agent starts ticked, once the shell has reported, unless the
  // person has a saved choice or has already touched the list.
  const [touched, setTouched] = useState(choices.harnesses !== undefined)
  useEffect(() => {
    if (touched || statuses === null) return
    setChoices((prev) => ({ ...prev, harnesses: readyHarnesses(statuses) }))
  }, [statuses, touched])
  const ticked = choices.harnesses ?? []

  const toggle = useCallback((id: string) => {
    setTouched(true)
    setChoices((prev) => {
      const now = prev.harnesses ?? []
      const harnesses = now.includes(id)
        ? now.filter((h) => h !== id)
        : [...now, id]
      saveChoices({ harnesses })
      return { ...prev, harnesses }
    })
  }, [])

  const apps = choices.apps ?? []
  const toggleApp = useCallback((lineageId: string) => {
    setChoices((prev) => {
      const now = prev.apps ?? []
      const next = now.includes(lineageId)
        ? now.filter((a) => a !== lineageId)
        : [...now, lineageId]
      saveChoices({ apps: next })
      return { ...prev, apps: next }
    })
  }, [])

  const finish = useCallback(() => {
    rememberComposerHarnesses(ticked)
    const first = defaultHarness(ticked)
    const id = first ? composerChoiceFor(first, local) : null
    if (id) rememberComposerModel(id)
    finishOnboarding(choices)
    onDone()
  }, [choices, local, onDone, ticked])

  // Agents first. Then apps, when the registry offered any or has not yet
  // answered: a slow registry shows a loading line, it does not skip the step.
  const hasApps = picks === 'pending' || picks.length > 0
  const start = useCallback(() => {
    if (step === 'agents' && hasApps) {
      setStep('apps')
      return
    }
    finish()
  }, [finish, hasApps, step])

  // On the apps step when the registry answers empty or fails: nothing to
  // offer, so the first run ends as Start would have ended it.
  useEffect(() => {
    if (step === 'apps' && picks !== 'pending' && picks.length === 0) finish()
  }, [finish, picks, step])

  const pickList = picks === 'pending' ? [] : picks

  const installAndFinish = useCallback(async () => {
    const chosen = pickList.filter((p) => apps.includes(p.lineageId))
    if (chosen.length === 0 || !install) {
      finish()
      return
    }
    setInstalling(true)
    const broke = await install(chosen).catch((cause: unknown) =>
      chosen.map((c) => ({ name: c.name, error: String(cause) })),
    )
    setInstalling(false)
    // A failed install is shown, not swallowed; the person can go on anyway,
    // and install it later from Discover.
    if (broke.length > 0) setFailed(broke)
    else finish()
  }, [apps, finish, install, pickList])

  const skip = useCallback(() => {
    finishOnboarding()
    onDone()
  }, [onDone])

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/85 backdrop-blur-sm">
      <div className="flex max-h-[92vh] w-[min(640px,94vw)] flex-col overflow-hidden rounded-xl border border-border bg-background shadow-2xl">
        {step === 'agents' ? (
          <>
            <header className="border-border/60 border-b px-5 py-3">
              <h1 className="font-medium text-[15px] text-foreground">
                Choose your agents
              </h1>
              <p className="mt-0.5 text-[12px] text-muted-foreground leading-snug">
                Mod works with the coding agents already on your Mac, on your
                own plans. Tick the ones you want to use; you pick between them
                when you type.
              </p>
            </header>

            <div className="min-h-0 flex-1 space-y-2 overflow-y-auto px-5 py-4">
              {rows.map((row) => (
                <AgentRow
                  key={row.id}
                  name={row.name}
                  detail={row.detail}
                  readiness={row.readiness}
                  ticked={ticked.includes(row.id)}
                  onToggle={() => toggle(row.id)}
                />
              ))}
              {statuses !== null && !anyReady && (
                <p className="pt-1 text-[12px] text-muted-foreground leading-snug">
                  None of these is ready on this Mac yet. Run one of the
                  commands above in Terminal; this list updates by itself.
                </p>
              )}
              {projectId && <ProjectInviteStep projectId={projectId} />}
            </div>

            <footer className="flex items-center gap-2 border-border/60 border-t px-5 py-3">
              <button
                type="button"
                onClick={skip}
                className="rounded px-2 py-1 text-[12px] text-muted-foreground/80 hover:bg-muted hover:text-foreground"
              >
                Skip
              </button>
              <div className="flex-1" />
              <button
                type="button"
                onClick={start}
                disabled={ticked.length === 0}
                className="rounded bg-foreground px-3 py-1 font-medium text-[12px] text-background disabled:cursor-not-allowed disabled:opacity-40"
              >
                {hasApps ? 'Next' : 'Start'}
              </button>
            </footer>
          </>
        ) : (
          <>
            <header className="border-border/60 border-b px-5 py-3">
              <h1 className="font-medium text-[15px] text-foreground">
                Start with some apps
              </h1>
              <p className="mt-0.5 text-[12px] text-muted-foreground leading-snug">
                Tick any you want in every project. You can add or remove apps
                later from Packages.
              </p>
            </header>

            <div className="min-h-0 flex-1 space-y-2 overflow-y-auto px-5 py-4">
              {picks === 'pending' && (
                <p className="text-[12px] text-muted-foreground">
                  Loading apps…
                </p>
              )}
              {pickList.map((pick) => (
                <AppPickRow
                  key={pick.lineageId}
                  entry={pick}
                  ticked={apps.includes(pick.lineageId)}
                  onToggle={() => toggleApp(pick.lineageId)}
                />
              ))}
              {failed.length > 0 && (
                <div role="alert" className="pt-1 text-[12px] text-red-500">
                  {failed.map((f) => (
                    <p key={f.name}>
                      {f.name} did not install: {f.error}
                    </p>
                  ))}
                </div>
              )}
            </div>

            <footer className="flex items-center gap-2 border-border/60 border-t px-5 py-3">
              <button
                type="button"
                onClick={() => setStep('agents')}
                className="rounded px-2 py-1 text-[12px] text-muted-foreground/80 hover:bg-muted hover:text-foreground"
              >
                Back
              </button>
              <div className="flex-1" />
              {failed.length > 0 ? (
                <button
                  type="button"
                  onClick={finish}
                  className="rounded bg-foreground px-3 py-1 font-medium text-[12px] text-background"
                >
                  Continue
                </button>
              ) : picks === 'pending' ? (
                <button
                  type="button"
                  onClick={finish}
                  className="rounded px-2 py-1 text-[12px] text-muted-foreground/80 hover:bg-muted hover:text-foreground"
                >
                  Skip
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => void installAndFinish()}
                  disabled={installing}
                  className="rounded bg-foreground px-3 py-1 font-medium text-[12px] text-background disabled:opacity-40"
                >
                  {installing
                    ? 'Installing…'
                    : apps.length > 0
                      ? `Install ${apps.length} and start`
                      : 'Start'}
                </button>
              )}
            </footer>
          </>
        )}
      </div>
    </div>
  )
}

function AgentRow({
  name,
  detail,
  readiness,
  ticked,
  onToggle,
}: {
  name: string
  detail: string
  readiness: Readiness | null
  ticked: boolean
  onToggle: () => void
}) {
  // Unknown (a browser) stays tickable; known and not ready does not, because
  // the composer would then offer an agent that cannot answer.
  const tickable = readiness === null || readiness.ready
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={!tickable}
      aria-pressed={ticked}
      className={`flex w-full flex-col gap-1 rounded-lg border p-2.5 text-left disabled:cursor-default ${
        ticked
          ? 'border-[color:var(--ws-border-focus)] bg-muted/60'
          : 'border-border enabled:hover:bg-muted/40'
      }`}
    >
      <span className="flex items-center gap-2">
        <span
          className={`flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-[3px] border ${
            ticked
              ? 'border-foreground bg-foreground text-background'
              : 'border-border'
          }`}
        >
          {ticked && <Check size={10} />}
        </span>
        <span className="font-medium text-[13px] text-foreground">{name}</span>
        {readiness && (
          <span
            className={`rounded px-1 py-px text-[10px] ${
              readiness.ready
                ? 'bg-muted text-foreground'
                : 'text-muted-foreground'
            }`}
          >
            {readiness.label}
          </span>
        )}
      </span>
      <span className="text-[11px] text-muted-foreground leading-snug">
        {detail}
      </span>
      {readiness && !readiness.ready && readiness.remedy && (
        <code className="mt-0.5 select-text rounded bg-muted/60 px-1.5 py-0.5 font-mono text-[11px] text-foreground">
          {readiness.remedy}
        </code>
      )}
    </button>
  )
}

/**
 * Invite people to the project behind the flow (RFC 0345 S14). Drawn only
 * when `send` exists, so it is never a form that cannot send. Each invite is
 * S4's: a sealed key on the auth-worker and a private link, which the person
 * copies to whoever they invited, as from the Share dropdown.
 */
function InviteStep({
  send,
}: {
  send: (email: string) => Promise<{ link: string }>
}) {
  const [email, setEmail] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [sent, setSent] = useState<Array<{ email: string; link: string }>>([])
  const [copied, setCopied] = useState('')
  return (
    <section className="space-y-2 border-border/60 border-t pt-3">
      <h2 className="font-medium text-[13px] text-foreground">Invite people</h2>
      <p className="text-[12px] text-muted-foreground leading-snug">
        They get this project, and can edit it. Send each one their private
        link.
      </p>
      <form
        className="flex gap-1.5"
        onSubmit={async (e) => {
          e.preventDefault()
          const address = email.trim().toLowerCase()
          setBusy(true)
          setError('')
          try {
            const { link } = await send(address)
            setSent((had) => [...had, { email: address, link }])
            setEmail('')
          } catch (err) {
            setError(err instanceof Error ? err.message : 'Invite failed')
          } finally {
            setBusy(false)
          }
        }}
      >
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Add people by email"
          aria-label="Email to invite"
          className="min-w-0 flex-1 rounded border border-border bg-transparent px-2 py-1 text-[12px]"
        />
        <button
          type="submit"
          disabled={busy}
          className="rounded border border-border px-2 text-[12px] hover:bg-muted disabled:opacity-40"
        >
          {busy ? '…' : 'Invite'}
        </button>
      </form>
      {error && (
        <p role="alert" className="text-[12px]">
          {error}
        </p>
      )}
      {sent.map((invite) => (
        <div
          key={invite.email}
          className="flex items-center justify-between gap-2 text-[12px]"
        >
          <span className="truncate">{invite.email}</span>
          <button
            type="button"
            className="shrink-0 rounded px-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            onClick={() => {
              void navigator.clipboard?.writeText(invite.link).then(() => {
                setCopied(invite.email)
                setTimeout(() => setCopied(''), 1500)
              })
            }}
          >
            {copied === invite.email ? 'Copied' : 'Copy link'}
          </button>
        </div>
      ))}
    </section>
  )
}

function ProjectInviteStep({ projectId }: { projectId: string }) {
  const invite = useProjectInviter(projectId)
  return invite ? <InviteStep send={invite} /> : null
}

function AppPickRow({
  entry,
  ticked,
  onToggle,
}: {
  entry: RegistryEntry
  ticked: boolean
  onToggle: () => void
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={ticked}
      className={`flex w-full flex-col gap-1 rounded-lg border p-2.5 text-left ${
        ticked
          ? 'border-[color:var(--ws-border-focus)] bg-muted/60'
          : 'border-border hover:bg-muted/40'
      }`}
    >
      <span className="flex items-center gap-2">
        <span
          className={`flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-[3px] border ${
            ticked
              ? 'border-foreground bg-foreground text-background'
              : 'border-border'
          }`}
        >
          {ticked && <Check size={10} />}
        </span>
        {entry.icon && (
          <IconTile
            spec={entry.icon}
            app={{ id: entry.lineageId, name: entry.name }}
            size={18}
          />
        )}
        <span className="font-medium text-[13px] text-foreground">
          {entry.name}
        </span>
        <span className="text-[10px] text-muted-foreground">
          {entry.category}
        </span>
      </span>
      <span className="text-[11px] text-muted-foreground leading-snug">
        {entry.description}
      </span>
    </button>
  )
}
